// Issue #185 — the confirmation shown before a traveler is removed, on a trip (the
// share dialog's "People with access") and on a template (its Travelers section).
//
// Removing someone takes rows with them: anything they were the *only* allocatee of is
// deleted, because nobody is left who could complete it. The counts come from the pure
// `previewParticipantRemoval` planner, so the dialog states them before the user
// commits — and the removal itself is deferred behind an Undo toast by the caller.

import { useState, type ReactNode } from "react";
import { Button, Dialog, vars } from "@pack-it-up/ui-web";
import { participantRemovalSummary } from "@pack-it-up/viewmodel";
import type { ParticipantRemovalPreview } from "@pack-it-up/core";

export interface RemoveParticipantDialogProps {
  displayName: string;
  /** What they are being removed from — only the wording differs. */
  resourceKind: "trip" | "template";
  preview: ParticipantRemovalPreview;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

export const RemoveParticipantDialog = ({
  displayName,
  resourceKind,
  preview,
  onConfirm,
  onClose,
}: RemoveParticipantDialogProps): ReactNode => {
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async (): Promise<void> => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Remove ${displayName}?`}
      subtitle={
        resourceKind === "trip"
          ? "They lose access to this trip."
          : "They stop being one of this template's default travelers."
      }
    >
      <p style={{ fontSize: vars.font.sizeSm, color: vars.color.text, margin: 0, marginBottom: vars.space.md }}>
        {participantRemovalSummary(preview, displayName)}
      </p>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: vars.space.sm }}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" disabled={confirming} onClick={() => void handleConfirm()}>
          Remove
        </Button>
      </div>
    </Dialog>
  );
};
